import type { ComponentType, ReactNode } from 'react';

import { isDev } from 'pkg/flags';
import { omit } from 'pkg/objects';
import { invokeMiddlewares } from 'pkg/router/invokeMiddlewares';
import type { MiddlewareFn } from 'pkg/router/middlewares';
import type { ParsedRoute } from 'pkg/router/parser';
import type { RouteProps } from 'pkg/router/route';
import * as utils from 'pkg/router/utils';
import { UrlParser } from 'pkg/url';

const urlParser = new UrlParser();

export interface DelegateProps {
	currentPath?: string;
	include?: Record<string, unknown>;
	notFoundComponent?: ComponentType<unknown> | null;
	errorComponent?: ComponentType<unknown> | null;
	beforeEach?: MiddlewareFn[];
	afterEach?: MiddlewareFn[];
	wrapAllWith?: ComponentType<{ children?: ReactNode }>;
}

export interface DelegateResult extends Partial<ParsedRoute> {
	notFound: boolean;
	hasError: boolean;
	hasMatch: boolean;
	component?: ComponentType<unknown>;
	[key: string]: unknown;
}

export function delegate(
	parsedRoutes: ParsedRoute[],
	props: DelegateProps
): DelegateResult {
	let {
		currentPath,
		include,
		notFoundComponent,
		errorComponent,
		beforeEach,
		afterEach,
	} = props;

	if (!notFoundComponent) notFoundComponent = null;
	if (!errorComponent) errorComponent = null;
	if (!include) include = {};
	if (!currentPath) currentPath = '/';

	if (currentPath.substring(0, 1) !== '/') {
		currentPath = `/${currentPath}`;
	}

	let component: ComponentType<unknown> | null = null;
	let routeMatch: ReturnType<UrlParser['parse']> | null = null;
	let hasMatch = false;
	let hasError = false;
	let notFound = false;
	let params: Record<string, unknown> = {};

	let route = parsedRoutes.find(({ path }) => {
		routeMatch = urlParser.parse(path, currentPath!);
		return routeMatch.match;
	});

	if (route && route.redirectTo) {
		const parsed = urlParser.parse(route.path, currentPath);
		const redirectPath = utils.transform(route.redirectTo, parsed.params);

		route = parsedRoutes.find(({ path }) => {
			routeMatch = urlParser.parse(path, `/${redirectPath}`);
			return routeMatch.match;
		});

		if (route) {
			window.history.replaceState({}, '', `/${redirectPath}`);
		}
	}

	try {
		if (route) {
			hasError = false;

			params = omit(
				{
					...route,
					...routeMatch,
					...routeMatch?.params,
					...include,
				},
				'params',
				'match'
			);

			params.query = {
				...(params.query as Record<string, unknown>),
				...Object.fromEntries(new URLSearchParams(window.location.search)),
			};

			params.urlParams = routeMatch?.params || {};

			const pathParts = (params.pattern as string)
				.split('/')
				.map((n: string) => n.replace(':', ''));

			hasMatch = pathParts.every((n: string) => {
				const morphedProp = `match${n.charAt(0).toUpperCase() + n.slice(1)}`;
				const pattern = params[morphedProp] as string | null;

				if (!pattern) return true;

				return new RegExp(`^${pattern}$`, 'i').test(params[n] as string);
			});

			if (!hasMatch) {
				notFound = true;
				component = notFoundComponent;
				return { notFound, hasError, component, hasMatch, ...params };
			}

			pathParts.forEach((n: string) => {
				const morphedProp = `transform${n.charAt(0).toUpperCase() + n.slice(1)}`;
				const transform = params[morphedProp] as
					| ((val: unknown) => unknown)
					| null;

				if (transform && typeof transform === 'function') {
					params[n] = transform(params[n]);
				} else if (!transform && n.endsWith('Id')) {
					params[n] = Number.parseInt(params[n] as string, 10);
				}
			});

			params = invokeMiddlewares(
				beforeEach,
				params as unknown as RouteProps
			) as unknown as Record<string, unknown>;

			if (route.before) {
				params = invokeMiddlewares(
					Array.from(route.before),
					params as unknown as RouteProps
				) as unknown as Record<string, unknown>;
			}

			params = omit(params, 'before', 'after');

			if (route.after) {
				invokeMiddlewares(Array.from(route.after), params as any, false);
			}

			invokeMiddlewares(afterEach, params as any, false);
		} else {
			notFound = true;
			hasMatch = false;

			if (isDev()) {
				console.error('Route path "%s" not found!', props.currentPath);
			}

			component = notFoundComponent;
		}
	} catch (error) {
		hasError = true;

		if (isDev()) {
			console.error(error);
		}

		component = errorComponent;
	}

	return {
		notFound,
		hasError,
		component,
		hasMatch,
		...params,
	};
}
